// src/pages/Dashboard.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Stack,
  Chip,
  Divider,
} from "@mui/material";
import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  CalendarMonth as CalendarMonthIcon,
  WorkOutline as WorkOutlineIcon,
  LockOpen as LockOpenIcon,
  PeopleAltOutlined as PeopleAltOutlinedIcon,
  PlaylistAddCheckOutlined as PlaylistAddCheckOutlinedIcon,
  PendingActionsOutlined as PendingActionsOutlinedIcon,
  PersonAddAlt1Outlined as PersonAddAlt1OutlinedIcon,
  GroupRemove as GroupRemoveIcon,
} from "@mui/icons-material";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, LabelList } from "recharts";
import { watchEvents } from "../api/events";
import { ref, onValue } from "firebase/database";
import { db } from "../firebase";

/* Small stat tile */
function StatCard({ label, value, icon }) {
  return (
    <Card
      sx={{
        borderRadius: 3,
        height: "100%",
        display: "flex",
        bgcolor: "background.paper",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
        },
      }}
    >
      <CardContent sx={{ display: "flex", gap: 2, alignItems: "center", flex: 1, px: 3, py: 2 }}>
        <Avatar sx={{ width: 44, height: 44, bgcolor: "primary.light", color: "primary.main", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
          {icon}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", letterSpacing: 0.5, fontSize: 14, mb: 0.5 }}>
            {label}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1, color: "text.primary" }}>
            {value}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

/* Hero pills */
function Pill({ title, value, subtitle, icon }) {
  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 3,
        width: "100%",
        background: "linear-gradient(145deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.08) 100%)",
        border: "1px solid rgba(255,255,255,0.2)",
        backdropFilter: "blur(12px)",
        color: "white",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          background: "linear-gradient(145deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.12) 100%)",
          transform: "translateY(-1px)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: "rgba(255,255,255,0.2)",
            color: "#fff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          {icon}
        </Avatar>
        <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.95 }}>
          {title}
        </Typography>
      </Stack>
      <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1, color: "white", mb: 0.5 }}>
        {value}
      </Typography>
      <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 500 }}>
        {subtitle}
      </Typography>
    </Box>
  );
}

const gradientHero =
  "radial-gradient(ellipse 1200px 800px at 15% -30%, #8B5CF6 0%, rgba(139,92,246,0.5) 30%, transparent 70%), radial-gradient(ellipse 900px 600px at 90% 10%, #2563EB 0%, rgba(37,99,235,0.4) 35%, transparent 65%), linear-gradient(135deg, #667eea 0%, #764ba2 50%, #0f172a 100%)";
const COLORS = ["#6366F1", "#06B6D4", "#F59E0B", "#10B981", "#EF4444", "#A855F7", "#EC4899", "#8B5CF6", "#3B82F6", "#EAB308"];

export default function Dashboard() {
  const [events, setEvents] = useState({});
  const [employees, setEmployees] = useState({});

  useEffect(() => watchEvents(setEvents), []);
  useEffect(() => {
    const unsub = onValue(ref(db, "/employees"), (snap) => setEmployees(snap.val() || {}));
    return () => unsub();
  }, []);

  const eventStats = useMemo(() => {
    const list = Object.values(events || {});
    const published = list.filter((e) => e.status === "PUBLISHED");
    const activeToday = published.filter((e) => {
      const now = Date.now();
      const start = e?.dates?.start || 0;
      const end = e?.dates?.end || 0;
      return now >= start && now <= end;
    });
    return { published: published.length, activeToday: activeToday.length };
  }, [events]);

  const empStats = useMemo(() => {
    const list = Object.values(employees || {});
    const total = list.length;
    const active = list.filter((e) => e.onboardingStatus !== "OFFBOARDED").length;
    const invited = list.filter((e) => e.onboardingStatus === "INVITED").length;
    const offboarded = list.filter((e) => e.onboardingStatus === "OFFBOARDED").length;
    const inProgress = list.filter((e) => e.onboardingStatus === "IN_PROGRESS").length;
    const completed = list.filter((e) => e.onboardingStatus === "COMPLETED").length;

    const deptMap = {};
    list.forEach((e) => {
      const dept = e?.submittedProfile?.employment?.department || "Unassigned";
      deptMap[dept] = (deptMap[dept] || 0) + 1;
    });
    const departments = Object.entries(deptMap).map(([name, value]) => ({ name, value }));

    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const recentJoins = list.filter((e) => (e.createdAt || 0) >= cutoff);
    return {
      total,
      active,
      invited,
      offboarded,
      inProgress,
      completed,
      departments,
      recentJoins,
    };
  }, [employees]);

  return (
    <Box sx={{ px: 0, py: 0, width: "100%", maxWidth: "100%" }}>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 4, color: "text.primary" }}>
        Admin Dashboard
      </Typography>

      {/* ================= HERO ================= */}
      <Box
        sx={{
          borderRadius: 4,
          p: { xs: 3, md: 4, lg: 5 },
          background: gradientHero,
          color: "white",
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          mb: 5,
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "linear-gradient(transparent, rgba(0,0,0,0.1))",
            pointerEvents: "none",
          },
        }}
      >
        <Grid container spacing={4} alignItems="stretch">
          <Grid item xs={12} lg={7}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
              <Chip 
                label="ADMIN ANALYTICS" 
                size="small" 
                sx={{ 
                  bgcolor: "rgba(255,255,255,0.2)", 
                  color: "white", 
                  fontWeight: 600,
                  borderRadius: 2,
                }} 
              />
              <Chip 
                label="Smart insights" 
                size="small" 
                sx={{ 
                  bgcolor: "rgba(255,255,255,0.2)", 
                  color: "white", 
                  fontWeight: 600,
                  borderRadius: 2,
                }} 
              />
            </Stack>
            <Typography variant="h2" sx={{ fontWeight: 900, lineHeight: 1.15, mb: 2, fontSize: { xs: "2.5rem", md: "3.5rem" } }}>
              Employee experience pulse
            </Typography>
            <Typography sx={{ opacity: 0.9, maxWidth: 600, mb: 4, fontSize: { xs: "1rem", md: "1.125rem" }, lineHeight: 1.6 }}>
              Track onboarding velocity, team balance, and event engagement to keep every cohort on schedule.
            </Typography>
          </Grid>
          <Grid item xs={12} lg={5}>
            <Grid container spacing={3} height="100%">
              <Grid item xs={6}>
                <Pill title="ACTIVE EMPLOYEES" value={empStats.active} subtitle="currently engaged" icon={<CheckCircleOutlineIcon />} />
              </Grid>
              <Grid item xs={6}>
                <Pill title="EVENTS LIVE TODAY" value={eventStats.activeToday} subtitle="running now" icon={<CalendarMonthIcon />} />
              </Grid>
              <Grid item xs={6}>
                <Pill title="HIRING DEPARTMENTS" value={empStats.departments.length} subtitle="tracking headcount" icon={<WorkOutlineIcon />} />
              </Grid>
              <Grid item xs={6}>
                <Pill title="INVITED" value={empStats.invited} subtitle="awaiting onboarding" icon={<LockOpenIcon />} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* ================= INSIGHTS OVERVIEW ================= */}
      <Box sx={{ mb: 5, width: '100%' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 4, color: "text.primary" }}>
          Insights Overview
        </Typography>
        <Box sx={{ display: 'flex', gap: 4, width: '100%', flexWrap: 'wrap' }}>
          {[
            {
              title: "Onboarding Performance",
              desc: "Completion velocity & average onboarding duration.",
              bg: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
              icon: <CheckCircleOutlineIcon sx={{ fontSize: 48, color: "white" }} />,
              stat1: { label: "Avg Duration", value: "1 days" },
              stat2: { label: "Completion Rate", value: empStats.total ? `${Math.round((empStats.completed / empStats.total) * 100)}%` : "—" },
            },
            {
              title: "Engagement Overview",
              desc: "Activity and participation in events.",
              bg: "linear-gradient(135deg, #06B6D4 0%, #2563EB 100%)",
              icon: <CalendarMonthIcon sx={{ fontSize: 48, color: "white" }} />,
              stat1: { label: "Live Events", value: eventStats.activeToday },
              stat2: { label: "7d Submissions", value: "—" },
            },
            {
              title: "Growth Metrics",
              desc: "New joins and department expansion.",
              bg: "linear-gradient(135deg, #10B981 0%, #14B8A6 100%)",
              icon: <WorkOutlineIcon sx={{ fontSize: 48, color: "white" }} />,
              stat1: { label: "New Joins (30d)", value: empStats.recentJoins.length },
              stat2: { label: "Departments", value: empStats.departments.length },
            },
          ].map((c, i) => (
            <Box key={i} sx={{ flex: 1, minWidth: 300, flexBasis: 'calc(33.333% - 8px)' }}>
              <Card
                sx={{
                  p: 4,
                  borderRadius: 3,
                  background: c.bg,
                  color: "white",
                  height: "100%",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": { 
                    transform: "translateY(-5px)", 
                    boxShadow: "0 20px 40px rgba(0,0,0,0.3)" 
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "4px",
                    background: "rgba(255,255,255,0.3)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  {c.icon}
                  <Typography variant="h6" sx={{ fontWeight: 700, ml: 2 }}>
                    {c.title}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ opacity: 0.9, mb: 3, lineHeight: 1.5 }}>
                  {c.desc}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 14, opacity: 0.85, mb: 0.5 }}>{c.stat1.label}</Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: 28, lineHeight: 1 }}>{c.stat1.value}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 14, opacity: 0.85, mb: 0.5 }}>{c.stat2.label}</Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: 28, lineHeight: 1 }}>{c.stat2.value}</Typography>
                  </Grid>
                </Grid>
              </Card>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ================= ANALYTICS + CHARTS ================= */}
      <Box sx={{ mb: 6, width: '100%', px: 0 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 4, color: "text.primary" }}>
          Analytics & Trends
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, width: '100%', flexWrap: 'wrap' }}>
          {/* Pie chart */}
          <Box sx={{ flex: 1, minWidth: 400, flexBasis: '50%' }}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: "0 8px 32px rgba(0,0,0,0.08)", 
              height: 450,
              overflow: "hidden",
              transition: "all 0.3s ease",
              "&:hover": { boxShadow: "0 12px 40px rgba(0,0,0,0.12)" },
              width: '100%'
            }}>
              <CardContent sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column", width: '100%' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: "text.primary" }}>
                  Employee Department Distribution
                </Typography>
                <Box sx={{ flex: 1, width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie 
                        data={empStats.departments} 
                        dataKey="value" 
                        nameKey="name" 
                        cx="50%" 
                        cy="50%" 
                        outerRadius={120} 
                        innerRadius={0}
                        label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                        labelLine={true}
                      >
                        {empStats.departments.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`${value} employees`, "Count"]} />
                      <Legend layout="horizontal" align="center" verticalAlign="bottom" wrapperStyle={{ paddingTop: '10px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Bar chart */}
          <Box sx={{ flex: 1, minWidth: 400, flexBasis: '50%' }}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: "0 8px 32px rgba(0,0,0,0.08)", 
              height: 450,
              overflow: "hidden",
              transition: "all 0.3s ease",
              "&:hover": { boxShadow: "0 12px 40px rgba(0,0,0,0.12)" },
              width: '100%'
            }}>
              <CardContent sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column", width: '100%' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: "text.primary" }}>
                  Onboarding Status
                </Typography>
                <Box sx={{ flex: 1, width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        { name: "Invited", count: empStats.invited },
                        { name: "In Progress", count: empStats.inProgress },
                        { name: "Completed", count: empStats.completed },
                        { name: "Offboarded", count: empStats.offboarded },
                      ]}
                      margin={{ top: 20, right: 30, left: 0, bottom: 40 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" angle={-30} textAnchor="end" height={60} interval={0} tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend verticalAlign="top" height={36} />
                      <Bar 
                        dataKey="count" 
                        radius={[8, 8, 0, 0]} 
                        fill="#6366F1" 
                        background={{ fill: "#f0f0f0" }}
                      >
                        <LabelList dataKey="count" position="top" fill="#6366F1" fontSize={12} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>

      {/* ================= EVENT OVERVIEW ================= */}
      <Box sx={{ mb: 6, width: '100%' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 4, color: "text.primary" }}>
          Events Overview
        </Typography>
        <Grid container spacing={4} sx={{ width: '100%' }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Published Events" value={eventStats.published} icon={<CalendarMonthIcon />} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Active Today" value={eventStats.activeToday} icon={<CheckCircleOutlineIcon />} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Submissions (7d)" value="—" icon={<WorkOutlineIcon />} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Completion Rate" value="—" icon={<PlaylistAddCheckOutlinedIcon />} />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 6 }} />
    </Box>
  );
}